import { Injectable, OnInit } from '@angular/core';
import { AuthService } from '@app/auth/services/auth.service';
import { ApiInventoryService } from '@app/core/services/api/api-inventory.service';
import { BehaviorSubject, EMPTY, forkJoin, iif, Observable, of, pipe, Subject } from 'rxjs';

import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  share,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { Store } from '@app/store';
import { ShoppingCart } from '@modules/ecommerce/shared/models';
import { ApiBasketService, BasketItem, BasketItemSimple } from '@shared/services/cart/api-basket.service';
import { CartItemIdentity, cartItemKey, feedItemIdentity, findCartItemIndex, isSameCartItem } from '@shared/services/cart/cart-item-identity';
import { LocalStorageService } from '@shared/services/storage';
import { BuyLoaderService } from '@app/core/services/common/buy-loader.service';

const cartKey = '__cart';

@Injectable()
export class ShoppingCartService implements OnInit {

  constructor(private store: Store,
              private basketService: ApiBasketService,
              private inventoryService: ApiInventoryService,
              private authService: AuthService,
              private buyLoaderService: BuyLoaderService,
              private localStorageService: LocalStorageService) {
    this.loadCartFromLocalStorage();
  }

  get basket() {
    return this.cart$.value;
  }

  /** Zapamiętuje wybór kupującego z konfiguratora ceny. `null` znaczy „brak wyceny". */
  zapamietajWariant(pracaId: number | null, variantId: number | null): void {
    this.wybranyWariant = {pracaId, variantId};
  }

  /** Wariant wybrany dla TEJ pracy; `null`, gdy wyboru nie było albo dotyczył innej. */
  wariantDlaPracy(pracaId: number | null): number | null {
    if (!this.wybranyWariant || this.wybranyWariant.pracaId !== pracaId) {
      return null;
    }

    return this.wybranyWariant.variantId;
  }

  basketsItem: any;
  loadUpdates$ = new BehaviorSubject(false);

  /**
   * Wariant wybrany w konfiguratorze ceny, wraz z pracą, której dotyczy.
   *
   * Przycisk „Kup" na karcie pracy jest zwykłym blokiem strony i obsługuje go `main-page`,
   * który konfiguratora nie zna. Bez tego nośnika wołał koszyk bez identyfikatora wariantu,
   * backend cofał się do edycji domyślnej i kupujący dostawał inną edycję, niż wybrał —
   * z jej ceną i wymiarami.
   *
   * Trzymamy razem z identyfikatorem pracy, żeby wybór z poprzednio oglądanej karty nie
   * przykleił się do następnej: konfigurator publikuje wybór dla swojej pracy, a `add()`
   * bierze go tylko wtedy, gdy dotyczy tej samej.
   */
  private wybranyWariant: { pracaId: number | null; variantId: number | null } | null = null;

  private cart$ = new BehaviorSubject(null);
  syncCart$ = this.authService.authState$.pipe(
    filter(Boolean),
    withLatestFrom(this.cart$),
    tap((_) => this.loadUpdates$.next(true)),
    switchMap(([, cart]: any[]) =>
      iif(
        () => (cart?.basketItems ?? []).length > 0,
        this.basketService.addItem({
          items: cart?.basketItems?.map((item) => {
            return {
              objectId: item.objectId,
              type: item.type,
              quantity: item.quantity,
              parent_type: item.parentType,
              variantId: item.variantId ?? null
            };
          }),
        }, 'a1'),
        of(cart)
      )
    ),
    switchMap((_) => this.basketService.basket()),
    tap((cart) => this.cart$.next(cart)),
    tap((_) => this.loadUpdates$.next(false))
  );
  logout$ = this.authService.authState$.pipe(
    filter((token) => token === null),
    tap((_) => this.deleteCartFromLocalStorage()),
    tap((_) => this.store.set('cart', undefined)),
    tap((_) => this.store.set('items', undefined)),
    tap(() => this.cart$.next(null))
  );
  private itemsList$ = new Subject<BasketItem[]>();
  itemsChanged$ = this.itemsList$.asObservable();
  items$ = this.itemsList$.pipe(
    withLatestFrom(this.cart$),
    pipe(
      tap((_) => this.loadUpdates$.next(true)),
      map(
        ([items, cart]: any[]) => {
          return new ShoppingCart({
            ...cart,
            createdAt: cart?.createdAt ?? new Date(),
            updatedAt: new Date(),
            basketItems: items,
            parentType: items.parentType
          });
        }
      ),
      tap((cart) => this.cart$.next(cart))
    ),
    tap((_) => this.loadUpdates$.next(false)),
    share()
  );
  add$ = this.itemsList$.pipe(
    concatMap(() => this.itemsList$.pipe(
      tap((_) => this.loadUpdates$.next(true)),
      map((items): BasketItemSimple[] => items.map((item) => {
          return {
            objectId: item.objectId,
            type: item.type,
            quantity: item.quantity,
            parentType: item.parentType,
            grupID: item.grupID,
            variantId: item.variantId ?? null
          };
        })
      ),
      switchMap((items: BasketItemSimple[]) =>
        this.basketService.addItem({items: items}, 'a2')
      ),
      switchMap((_) => this.basketService.basket()),
      tap((cart) => this.cart$.next(cart)),
      tap((_) => this.loadUpdates$.next(false))
    ))
  );
  private updated$ = new Subject<BasketItem>();
  private added$ = new Subject<BasketItem>();
  private removed$ = new Subject();
  private pendingAddKeys = new Set<string>();

  shoppingCart$ = this.cart$.pipe(
    filter(Boolean),
    distinctUntilChanged(),
    tap((_) => this.loadUpdates$.next(true)),
    map((cart: any) => new ShoppingCart({
      ...cart,
      createdAt: cart?.createdAt ?? new Date(),
      updatedAt: new Date(),
    })),
    // update cart
    tap((cart: any) => this.store.set('cart', cart)),
    // save local
    tap((cart: any) => {
      return this.saveCartToLocalStorage(cart);
    }),
    // check status and map
    mergeMap(({basketItems: items}) => iif(
      () => (items ?? []).length > 0,
      // @TODO DEPRECATED (node_modules/rxjs/src/internal/observable/forkJoin.ts:13)
      forkJoin(items?.map(this.maps)),
      of(items)
    )),
    // save items
    tap((_) => this.loadUpdates$.next(false)),
    tap((items: BasketItem[]) => this.store.set('items', items)),
    share()
  );

  ngOnInit(): void {

  }

  /**
   * Dokłada pozycję do koszyka.
   *
   * `variantId` wskazuje wybraną edycję i opcje dodatkowe pracy wariantowej. Pozycję
   * rozpoznajemy po pełnej tożsamości, nie po samym identyfikatorze pracy: dwie edycje
   * tej samej pracy są dwoma produktami o różnych cenach, więc muszą zająć dwie pozycje
   * koszyka. Praca w trybie prostym wariantu nie ma i przechodzi tą samą drogą.
   */
  async add(objectId: number, itemType: string, parentType: string, grupID: string, variantId: number | null = null) {
    const tozsamosc = {objectId, type: itemType, variantId};
    const addKey = [cartItemKey(tozsamosc), parentType, grupID].join(':');
    if (this.pendingAddKeys.has(addKey)) {
      return;
    }

    this.pendingAddKeys.add(addKey);

    try {
      const items = this.store.value.items ?? [];
      // Prototyp pyta o wariant, bo od niego zależy pula sztuk: `maxQuantity` liczy się
      // na wskazanej edycji, a nie na sumie nakładu całej pracy.
      const prototype = await this.basketService.itemPrototype({
        objectId: objectId,
        quantity: 1,
        type: itemType,
        parentType: parentType,
        grupID: grupID,
        variantId: variantId
      });

      // update quantity
      const basketItems = items.map((inventory: BasketItem) => {
        inventory.parentType = parentType;
        if (isSameCartItem(inventory, tozsamosc)) {
          if (inventory.quantity < prototype.maxQuantity) {
            inventory.quantity++;
            this.updated$.next(inventory);

          }
        }
        return inventory;
      });

      // add item if not exist in the basket
      const item = items?.find((x) => isSameCartItem(x, tozsamosc));
      if (item === undefined) {
        const inventory = {
          ...prototype,
          objectId: objectId,
          quantity: 1,
          type: itemType,
          parentType: parentType,
          variantId: variantId
        };
        basketItems.push(inventory);
        this.added$.next(inventory);
      }
      this.itemsList$.next(basketItems);
    } catch (error) {
      console.error('Error please contact the administrator', error);
      this.buyLoaderService.hideLoader();
      throw error;
    } finally {
      this.pendingAddKeys.delete(addKey);
    }
  }

  update(items: any) {
    this.itemsList$.next(items.items);
    if (items.item) {
      this.updated$.next(items.item);
    }
  }

  delete(item: BasketItem) {
    // remove if user logged in
    this.authService.authState$
      .pipe(
        mergeMap((token) =>
          iif(() => !!token, this.basketService.deleteItem(item.id), of(null))
        ),
        take(1)
      )
      .subscribe(() => this.remove(item));
  }

  finalize() {
    this.cart$.next(new ShoppingCart({}));
  }

  loadCartFromLocalStorage() {
    const state = !!this.authService.state.getValue();
    if (state) {
      return;
    }

    const cartItem = this.localStorageService.getItem(cartKey);
    const shoppingCart = JSON.parse(cartItem);
    if (shoppingCart === null) {
      return;
    }

    const cart = new ShoppingCart({...shoppingCart});
    this.cart$.next(cart);
    this.store.set('cart', cart);
    this.store.set('items', cart.basketItems);
  }

  saveCartToLocalStorage(cart: ShoppingCart) {
    this.localStorageService.setItem(cartKey, JSON.stringify(cart));
  }

  getBascietsItems() {
    const state = !!this.authService.state.getValue();
    if (!state) {
      // Odświeżamy koszyk lokalny po wejściu w koszyk, żeby ceny nie zostały
      // na kursie zapisanym wcześniej w localStorage.
      return this.refreshCartItems2(this.basket).pipe(debounceTime(1000), take(1)).subscribe();
    }
  }


  refreshCart() {
    return this.refreshCartItems(this.basket).pipe(debounceTime(1000), take(1));
  }

  deleteCartFromLocalStorage() {
    this.localStorageService.removeItem(cartKey);
  }

  private maps = (item: BasketItem) => this.inventoryService.buyIsAvailable(item.objectId).then((result) => {
    const _item: BasketItem = {
      ...item,
      status: !result ? 'unavailable' : undefined,
    };
    return _item;
  });

  private refreshCartItems2 = (cart: ShoppingCart): Observable<ShoppingCart> => {
    if ((cart?.basketItems || []).length === 0) {
      return EMPTY;
    }
  
    const basketItems: BasketItem[] = cart.basketItems.map(item => ({
      objectId: item.objectId,
      quantity: item.quantity,
      type: item.type,
      // Bez wariantu feed odesłałby kwoty odbicia edycji domyślnej, więc kupujący
      // widziałby w koszyku cenę i wymiary innej edycji, niż wybrał.
      variantId: item.variantId ?? null,
      additionalInformations: item.additionalInformations ?? null,
      defaultShippingCurrency: item.defaultShippingCurrency ?? null,
      insurance: item.insurance ?? null,
      parentType: item.parentType ?? null,
      grupID: item.grupID ?? null
    }));
  
    return this.basketService.getItemBaskets(basketItems).pipe(
      map((_items) => new ShoppingCart({
        ...cart,
        basketItems: this.zszyjPozycjeZFeedem(basketItems, _items),
      })),
      tap((_cart) => this.cart$.next(_cart)),
      tap((_cart) => this.saveCartToLocalStorage(_cart))
    );
  };

  /**
   * Zestawia pozycje koszyka z odpowiedzią feedu.
   *
   * Odpowiedź bywa krótsza od żądania: backend pomija pozycję, której wariantu nie
   * potrafi wycenić, i pozycję, której edycja nie ma tylu sztuk. Zestawianie po pozycji
   * w tablicy przepisywało wtedy pozostałym pozycjom ilość i wariant sąsiada, a wynik
   * wraca do pamięci przeglądarki i idzie w zamówieniu gościa — kupujący dostawał inną
   * edycję albo inną liczbę sztuk, niż wybrał. Dlatego odpowiedź szukamy po tożsamości
   * pozycji; feed nie ma `objectId`, identyfikator pracy niesie w `inventoryWorkId`.
   *
   * Pozycji bez odpowiedzi nie usuwamy. Brak odpowiedzi mówi „dziś nie do sprzedania",
   * a nie „pozycja martwa": wyprzedana edycja wraca do sprzedaży, gdy klient dołoży
   * sztuk, a backend usuwa pozycję z koszyka dopiero wtedy, gdy zniknęła cała praca.
   * Pozycja zostaje więc z własną ilością i wariantem, oznaczona jako niedostępna.
   *
   * @param pozycjeKoszyka pozycje wysłane w żądaniu feedu
   * @param odpowiedzFeedu pozycje zwrócone przez feed
   * @return pozycje koszyka w kolejności żądania, każda z własną tożsamością i ilością
   */
  private zszyjPozycjeZFeedem(pozycjeKoszyka: BasketItem[], odpowiedzFeedu: any[]): BasketItem[] {
    const feedPoTozsamosci = new Map<string, any>();
    (Array.isArray(odpowiedzFeedu) ? odpowiedzFeedu : [])
      .filter(Boolean)
      .forEach((pozycja) => feedPoTozsamosci.set(cartItemKey(feedItemIdentity(pozycja)), pozycja));

    return pozycjeKoszyka.map((pozycjaKoszyka): BasketItem => {
      const zFeedu = feedPoTozsamosci.get(cartItemKey(pozycjaKoszyka));
      if (!zFeedu) {
        return {...pozycjaKoszyka, status: 'unavailable'};
      }

      // Feed odpowiada za kwoty i opis pozycji; tożsamość i liczba sztuk zostają te,
      // które wybrał kupujący.
      return {
        ...pozycjaKoszyka,
        ...zFeedu,
        objectId: zFeedu.objectId ?? zFeedu.inventoryWorkId ?? pozycjaKoszyka.objectId,
        quantity: pozycjaKoszyka.quantity,
        type: zFeedu.type ?? pozycjaKoszyka.type,
        variantId: zFeedu.variantId ?? pozycjaKoszyka.variantId ?? null,
        parentType: zFeedu.parentType ?? pozycjaKoszyka.parentType,
        grupID: zFeedu.grupID ?? pozycjaKoszyka.grupID,
      };
    });
  }

  // private refreshCartItems2 = (cart: ShoppingCart): Observable<ShoppingCart> => {
  //   if ((cart?.basketItems || []).length === 0) {
  //     return EMPTY;
  //   }
  //   const idItems = [];
  //   const items = forkJoin(
  //     cart.basketItems.map((item) => {
  //         idItems.push({objectId: item.objectId, quantity: item.quantity, type: item.type});
  //       }
  //     )
  //   );
  //   return items.pipe(
  //     map((_items) => new ShoppingCart({
  //       ...cart,
  //       basketItems: _items,
  //     })),
  //     tap((_cart) => this.cart$.next(_cart))
  //   );
  // };

  private refreshCartItems = (cart: ShoppingCart): Observable<ShoppingCart> => {
    if ((cart?.basketItems || []).length === 0) {
      return EMPTY;
    }
    const items = forkJoin(
      cart.basketItems.map((item) =>
        this.basketService.itemPrototype({
          objectId: item.objectId,
          quantity: item.quantity,
          type: item.type,
          parentType: item.parentType,
          grupID: item.grupID,
          variantId: item.variantId ?? null
        })
      )
    );
    return items.pipe(
      map((_items) => new ShoppingCart({
        ...cart,
        basketItems: _items,
      })),
      tap((_cart) => this.cart$.next(_cart))
    );
  };

  // private refreshCartItems = (cart: ShoppingCart): Observable<ShoppingCart> => {
  //   if ((cart?.basketItems || []).length === 0) {
  //     return EMPTY;
  //   }
  //   const idItems = [];
  //   const items = forkJoin(
  //     cart.basketItems.map((item) => {
  //         idItems.push({objectId: item.objectId, quantity: item.quantity, type: item.type, parentType: item.parentType});
  //         this.basketService.itemPrototype({
  //           objectId: item.objectId,
  //           quantity: item.quantity,
  //           type: item.type,
  //           parentType: item.parentType,
  //           grupID: item.grupID
  //         });
  //       }
  //     )
  //   );
  //   return items.pipe(
  //     map((_items) => new ShoppingCart({
  //       ...cart,
  //       basketItems: _items,
  //     })),
  //     tap((_cart) => this.cart$.next(_cart))
  //   );
  // };


  /**
   * Wyrzuca z koszyka wskazaną pozycję.
   *
   * Dopasowanie po samym identyfikatorze pracy zabierało pierwszą pozycję tej pracy,
   * a nie tę, którą kupujący usuwał — przy dwóch edycjach w koszyku znikała nie ta,
   * którą wskazał.
   */
  private remove(identity: CartItemIdentity) {
    const items = JSON.parse(localStorage.getItem('__cart')).basketItems;
    const index = findCartItemIndex(items, identity);
    const removedItem = items[index];
    if (index > -1) {
      items.splice(index, 1);
    }
    this.removed$.next(removedItem);
    this.itemsList$.next(items);
  }
}
